export const MAX_ITERATIONS = 1024;
export const SMOOTHING_STEPS = 3;
const LOG2_INV = 1 / Math.log(2);

// Shared by the main thread and workers. Keep the shader's equivalent in sync.
// A budget of N permits N recurrence steps and checks z_N for strict escape.
// The separate flag matters when escape occurs on the last permitted step.
export function iterateUntilEscape(x0, y0, result, maxIterations = MAX_ITERATIONS, useMu = true) {
  // Skip the known interior of the main cardioid and period-2 bulb. This keeps
  // the increased boundary detail budget inexpensive in overview images.
  const q = (x0 - 0.25) ** 2 + y0 * y0;
  if (q * (q + x0 - 0.25) <= 0.25 * y0 * y0 || (x0 + 1) ** 2 + y0 * y0 <= 0.0625) {
    result.iterations = maxIterations;
    result.escaped = false;
    result.mu = 0;
    return result;
  }
  let x = 0, y = 0, x2 = 0, y2 = 0, iteration = 0;
  while (x2 + y2 <= 4 && iteration < maxIterations) {
    y = 2 * x * y + y0;
    x = x2 - y2 + x0;
    x2 = x * x;
    y2 = y * y;
    iteration++;
  }
  result.iterations = iteration;
  result.escaped = x2 + y2 > 4;
  result.mu = 0;
  if (result.escaped && useMu) {
    // Refine the escaped orbit before evaluating the continuous count. At
    // radius 2 alone, the approximation still produces visible contour seams.
    for (let i = 0; i < SMOOTHING_STEPS; i++) {
      y = 2 * x * y + y0;
      x = x2 - y2 + x0;
      x2 = x * x;
      y2 = y * y;
    }
    result.mu = iteration + SMOOTHING_STEPS + 1 - Math.log(0.5 * Math.log(x2 + y2)) * LOG2_INV;
  }
  return result;
}

// Used only for compact views around the period-3 bulbs. The historical
// iterator above stays on the overview and exterior paths.
export function iterateUntilEscapePeriodic(x0, y0, result, maxIterations = MAX_ITERATIONS, useMu = true) {
  const q = (x0 - 0.25) ** 2 + y0 * y0;
  if (q * (q + x0 - 0.25) <= 0.25 * y0 * y0 || (x0 + 1) ** 2 + y0 * y0 <= 0.0625) {
    result.iterations = maxIterations;
    result.escaped = false;
    result.mu = 0;
    return result;
  }
  let x = 0, y = 0, x2 = 0, y2 = 0, iteration = 0;
  let periodicX = 0, periodicY = 0, periodicCheckpoint = 32;
  while (x2 + y2 <= 4 && iteration < maxIterations) {
    y = 2 * x * y + y0;
    x = x2 - y2 + x0;
    x2 = x * x;
    y2 = y * y;
    iteration++;
    if (iteration >= 32 && x === periodicX && y === periodicY &&
        Object.is(x, periodicX) && Object.is(y, periodicY) && x2 + y2 <= 4) {
      // An identical finite state and identical c give the same future orbit.
      // Object.is distinguishes signed zero; === excludes NaN.
      result.iterations = Math.ceil(maxIterations);
      result.escaped = false;
      result.mu = 0;
      return result;
    }
    if (iteration === periodicCheckpoint) {
      periodicX = x;
      periodicY = y;
      periodicCheckpoint *= 2;
    }
  }
  result.iterations = iteration;
  result.escaped = x2 + y2 > 4;
  result.mu = 0;
  if (result.escaped && useMu) {
    for (let i = 0; i < SMOOTHING_STEPS; i++) {
      y = 2 * x * y + y0;
      x = x2 - y2 + x0;
      x2 = x * x;
      y2 = y * y;
    }
    result.mu = iteration + SMOOTHING_STEPS + 1 - Math.log(0.5 * Math.log(x2 + y2)) * LOG2_INV;
  }
  return result;
}
